import { createErrorResponseStandard, createResponseStandard, orgId, isHttpSuccess } from "utils/functions";
import { gatewayClient } from "./gatewayServiceClients";
import { Registry } from "types/Gateways";
import WebsocketQuery from "./websocketQuery";
import LocalStorageService from "./localStorageService";
import moment from "moment";
import AxiosInstance from "./axiosInstance";
import { NetworkOBJ } from "constant/gateway";

const WS = new WebsocketQuery();

export const addNetwork = async (
    netId: string,
    networkName: string
) => {
    try {
        const body: any = {
            organization_id: orgId(),
            name: networkName,
            net_id: netId,
            type: NetworkOBJ,
        };
        const response = await gatewayClient.createRegisteredGw(body);
        return createResponseStandard<Registry>(response);
    } catch (error) {
        return createErrorResponseStandard(error);
    }
};

/**
 * Query digital input data using PromQL to count unique UIDs
 * @param gatewayIds - Array of gateway IDs to query
 * @param uids - Array of UIDs (room/zone/group/hub names) to query
 * @param saids - Array of SAID values (default: ["0"])
 * @returns Number of unique UIDs found
 */
export const queryDigitalInputCount = async (
    gatewayIds: string[],
    uids: string[],
    saids: string[] = ["0"]
): Promise<number> => {
    try {
        const orgId = LocalStorageService.getItem("orgId");

        if (!WS.isReady(orgId)) {
            await WS.initConnection(orgId);
        }

        // Build query using exact match format with time range
        // Format: mqtt2tsdb_digitalinput_na{gateway="X",uid="Y",said="0"}[1d]
        // Note: Space after comma in selector (gateway="X", uid="Y")
        const uidQueries = uids.map(uid => {
            // Escape quotes in values
            const escapedGateway = gatewayIds[0].replaceAll('"', String.raw`\"`);
            const escapedUid = String(uid).replaceAll('"', String.raw`\"`);
            const escapedSaid = String(saids[0]).replaceAll('"', String.raw`\"`);

            // Format: {gateway="X", uid="Y", said="0"}[1d] - space after comma, with time range
            return `mqtt2tsdb_digitalinput_na{gateway="${escapedGateway}", uid="${escapedUid}", said="${escapedSaid}"}[1d]`;
        });

        // Join with OR operator (like buildPromQL and buildCombinedConsumptionWSQuery)
        const query = uidQueries.length > 1
            ? `(${uidQueries.join(" or ")})`
            : uidQueries[0];

        const params = {
            query: query,
            time: moment().unix(),
        };

        const response = await WS.fetch(params);

        // Extract result data from response structure (WebSocket returns { data: [...] })
        const resultData = response?.data || response?.result || [];
        const dataArray = Array.isArray(resultData) ? resultData : [];

        // Count unique UIDs (rooms/zones/groups/hubs) for this gateway
        const uniqueUIDs = new Set<string>();
        for (const item of dataArray) {
            const metric = item.metric || item;
            if (metric.gateway && metric.uid) {
                // Only count if it matches one of our gateway IDs
                if (gatewayIds.includes(metric.gateway)) {
                    uniqueUIDs.add(metric.uid);
                }
            }
        }

        return uniqueUIDs.size;
    } catch (error) {
        // Return default value on error - error is intentionally unused
        if (error instanceof Error) {
            // Error handled by returning default value
        }
        return 0;
    }
};

/**
 * Extract and parse status value from response data item
 * @param statusValue - The value from the response data item
 * @returns Parsed status number or null if invalid
 */
const parseStatusValue = (statusValue: any): number | null => {
    if (Array.isArray(statusValue) && statusValue.length > 1) {
        // value is [timestamp, status_value]
        const status = Number(statusValue[1]);
        return Number.isNaN(status) ? null : status;
    }

    if (typeof statusValue === 'number') {
        return statusValue;
    }

    if (typeof statusValue === 'string') {
        // Try to parse string value
        const status = Number(statusValue);
        return Number.isNaN(status) ? null : status;
    }

    return null;
};

/**
 * Query network status using WebSocket
 * @param gatewayId - Gateway/Network ID
 * @param uid - Device UID to query (optional, defaults to gateway ID)
 * @returns Status value from the query response, or null if error
 */
export const queryNetworkStatus = async (
    gatewayId: string,
    uid?: string
): Promise<number | null> => {
    try {
        const orgId = LocalStorageService.getItem("orgId");

        if (!WS.isReady(orgId)) {
            await WS.initConnection(orgId);
        }

        // Use provided UID or default to gateway ID
        const deviceUID = uid ?? gatewayId;

        // Escape quotes in values
        const escapedGateway = gatewayId.replaceAll('"', String.raw`\"`);
        const escapedUid = String(deviceUID).replaceAll('"', String.raw`\"`);

        // Format: mqtt2tsdb_digitalinput_na{gateway="X",uid="Y",said="0"} (no spaces after commas)
        const query = `mqtt2tsdb_digitalinput_na{gateway="${escapedGateway}",uid="${escapedUid}",said="0"}`;

        const params = {
            query: query,
            time: moment().unix(),
        };

        const response = await WS.fetch(params);

        // Extract status value from response
        // Response structure: { data: [{ metric: {...}, value: [timestamp, value] }] }
        const resultData = response?.data || response?.result || [];
        const dataArray = Array.isArray(resultData) ? resultData : [];

        if (dataArray.length === 0) {
            return null;
        }

        // Get the latest value (last item in array)
        const latestData = dataArray.at(-1);
        const statusValue = latestData?.value;

        return parseStatusValue(statusValue);
    } catch (error) {
        // Return default value on error - error is intentionally unused
        if (error instanceof Error) {
            // Error handled by returning default value
        }
        return null;
    }
};

/**
 * Extract UID-SAID pairs from tags API response
 * Tag format: { devices: [["gatewayId", "UID", SAID], ...] }
 */
const extractUIDSaidPairs = (tags: any[]): Array<{ uid: string; said: string }> => {
    const uidSaidPairs: Array<{ uid: string; said: string }> = [];

    for (const tag of tags) {
        if (!tag.devices || !Array.isArray(tag.devices)) {
            continue;
        }

        for (const device of tag.devices) {
            if (Array.isArray(device) && device.length >= 3) {
                const uid = String(device[1]);
                const said = String(device[2] ?? 0);
                uidSaidPairs.push({ uid, said });
            }
        }
    }

    return uidSaidPairs;
};

/**
 * Fetch tags from API and extract UID-SAID pairs
 */
const fetchTagUIDs = async (
    tagName: string,
    organizationId: string
): Promise<Array<{ uid: string; said: string }> | null> => {
    const tagsRequestParams = {
        organisation_id: organizationId,
        value: tagName,
        with_relationship: true,
    };

    const tagsResponse = await AxiosInstance.get("/tags", {
        params: tagsRequestParams,
    });

    if (!isHttpSuccess(tagsResponse.status)) {
        return null;
    }

    const tags = tagsResponse.data?.data || [];

    if (tags.length === 0) {
        return null;
    }

    const uidSaidPairs = extractUIDSaidPairs(tags);

    return uidSaidPairs.length > 0 ? uidSaidPairs : null;
};

/**
 * Build PromQL query string from UID-SAID pairs
 */
const buildPromQLQuery = (gatewayId: string, uidSaidPairs: Array<{ uid: string; said: string }>): string => {
    const escapedGateway = gatewayId.replaceAll('"', String.raw`\"`);

    const queries = uidSaidPairs.map(({ uid, said }) => {
        const escapedUid = String(uid).replaceAll('"', String.raw`\"`);
        const escapedSaid = String(said).replaceAll('"', String.raw`\"`);
        const query = `mqtt2tsdb_digitalinput_na{gateway="${escapedGateway}", uid="${escapedUid}", said="${escapedSaid}"}`;
        return query;
    });

    const query = queries.length > 1 ? queries.join(" OR ") : queries[0];

    return query;
};

/**
 * Check if a device status value indicates online status
 */
const isDeviceOnline = (statusValue: any): boolean => {
    if (Array.isArray(statusValue) && statusValue.length > 1) {
        const status = String(statusValue[1]);
        return status !== "0" && status !== "0.0";
    }

    if (typeof statusValue === 'string') {
        return statusValue !== "0" && statusValue !== "0.0";
    }

    if (typeof statusValue === 'number') {
        return statusValue !== 0;
    }

    return false;
};

type TagOnOffStatus = "On" | "Off";
type TagStatus = TagOnOffStatus | "Unknown";

/**
 * Determine tag status from WebSocket response
 * When there is no data, treat status as "Unknown" instead of "Off".
 */
const determineTagStatusFromResponse = (response: any): TagStatus => {
    const resultData = response?.data || response?.result || [];
    const dataArray = Array.isArray(resultData) ? resultData : [];

    if (dataArray.length === 0) {
        return "Unknown";
    }

    for (const item of dataArray) {
        const statusValue = item?.value;

        if (isDeviceOnline(statusValue)) {
            return "On";
        }
    }

    return "Off";
};

/**
 * Determine tag status and latest timestamp from WebSocket response
 * Returns:
 * - status: "On" if any device is online, otherwise "Off"
 * - timestamp: latest sample timestamp (in Unix seconds) across all returned series, or null
 */
const updateLatestTimestamp = (
    statusValue: any,
    latestTimestamp: number | null
): number | null => {
    if (!Array.isArray(statusValue) || statusValue.length === 0) {
        return latestTimestamp;
    }

    const ts = Number(statusValue[0]);
    if (Number.isNaN(ts)) {
        return latestTimestamp;
    }

    if (latestTimestamp === null || ts > latestTimestamp) {
        return ts;
    }

    return latestTimestamp;
};

const determineTagStatusAndTimestampFromResponse = (
    response: any
): { status: TagStatus; timestamp: number | null } => {
    const resultData = response?.data || response?.result || [];
    const dataArray = Array.isArray(resultData) ? resultData : [];

    if (dataArray.length === 0) {
        return { status: "Unknown", timestamp: null };
    }

    let latestTimestamp: number | null = null;
    let anyOnline = false;

    for (const item of dataArray) {
        const statusValue = item?.value;

        latestTimestamp = updateLatestTimestamp(statusValue, latestTimestamp);

        if (isDeviceOnline(statusValue)) {
            anyOnline = true;
        }
    }

    const result: { status: TagStatus; timestamp: number | null } = {
        status: anyOnline ? "On" : "Off",
        timestamp: latestTimestamp,
    };
    return result;
};

/**
 * Query tag status using PromQL WebSocket query (generic function for room, zone, group, hub)
 * Fetches UID-SAID pairs from tags API and queries status for all devices in the tag
 * @param gatewayId - Gateway/Network ID
 * @param tagName - Tag name (e.g., "room_A", "zone_A", "group_A", "hub_A")
 * @param tagType - Type of tag: "room", "zone", "group", or "hub"
 * @param organizationId - Organization ID
 * @returns "On" if any device is online, "Off" if all devices are offline, "Unknown" on error
 */
export const queryTagStatus = async (
    gatewayId: string,
    tagName: string,
    _tagType: "room" | "zone" | "group" | "hub",
    organizationId: string
): Promise<TagStatus> => {
    try {
        // Step 1: Fetch tags API to get UID-SAID pairs for the tag
        const uidSaidPairs = await fetchTagUIDs(tagName, organizationId);
        if (!uidSaidPairs) {
            return "Unknown";
        }

        // Step 2: Build PromQL query and execute via WebSocket
        const orgId = LocalStorageService.getItem("orgId");

        if (!WS.isReady(orgId)) {
            await WS.initConnection(orgId);
        }

        const query = buildPromQLQuery(gatewayId, uidSaidPairs);
        const params = {
            query: query,
            time: moment().unix(),
        };

        const response = await WS.fetch(params);

        // Step 3: Parse response to determine tag status
        return determineTagStatusFromResponse(response);
    } catch (error) {
        // Return default value on error - error is intentionally unused
        if (error instanceof Error) {
            // Error handled by returning default value
        }
        return "Unknown";
    }
};

/**
 * Query tag status using a precomputed UID→SAID map (same semantics as NetworkDetails)
 * and return both status and latest timestamp.
 */
export const queryTagStatusFromUIDSAIDsWithTimestamp = async (
    gatewayId: string,
    buildQueryFromUIDSAIDs: (gatewayId: string) => string | null
): Promise<{ status: TagStatus; timestamp: number | null }> => {
    try {
        const query = buildQueryFromUIDSAIDs(gatewayId);
        if (!query) {
            return { status: "Unknown", timestamp: null };
        }

        const orgId = LocalStorageService.getItem("orgId");
        if (!WS.isReady(orgId)) {
            await WS.initConnection(orgId);
        }

        const params = {
            query,
            time: moment().unix(),
        };

        const response = await WS.fetch(params);
        return determineTagStatusAndTimestampFromResponse(response);
    } catch (error) {
        // Return default value on error - error is intentionally unused
        if (error instanceof Error) {
            // Error handled by returning default value
        }
        return { status: "Unknown", timestamp: null };
    }
};

/**
 * Query tag status using PromQL WebSocket query and also return latest sample timestamp.
 * Returns an object: { status: "On" | "Off" | "Unknown", timestamp: number | null }
 */
export const queryTagStatusWithTimestamp = async (
    gatewayId: string,
    tagName: string,
    tagType: "room" | "zone" | "group" | "hub",
    organizationId: string
): Promise<{ status: TagStatus; timestamp: number | null }> => {
    try {
        // Step 1: Fetch tags API to get UID-SAID pairs for the tag
        const uidSaidPairs = await fetchTagUIDs(tagName, organizationId);
        if (!uidSaidPairs) {
            return { status: "Unknown", timestamp: null };
        }

        // Step 2: Build PromQL query and execute via WebSocket
        const orgId = LocalStorageService.getItem("orgId");

        if (!WS.isReady(orgId)) {
            await WS.initConnection(orgId);
        }

        const query = buildPromQLQuery(gatewayId, uidSaidPairs);
        const params = {
            query: query,
            time: moment().unix(),
        };

        const response = await WS.fetch(params);

        // Step 3: Parse response to determine tag status and timestamp
        const { status, timestamp } = determineTagStatusAndTimestampFromResponse(
            response
        );

        // determineTagStatusAndTimestampFromResponse will return:
        // - "On"/"Off" when data is present
        // - "Unknown" (with null timestamp) when the response contains no data
        return { status, timestamp };
    } catch (error) {
        // Return default value on error - error is intentionally unused
        if (error instanceof Error) {
            // Error handled by returning default value
        }
        return { status: "Unknown", timestamp: null };
    }
};

/**
 * Query room status using PromQL WebSocket query
 * Fetches UID-SAID pairs from tags API and queries status for all devices in the room
 * @param gatewayId - Gateway/Network ID
 * @param roomName - Room name (e.g., "room_A")
 * @param organizationId - Organization ID
 * @returns "On" if any device is online, "Off" if all devices are offline, "Unknown" on error
 */
export const queryRoomStatus = async (
    gatewayId: string,
    roomName: string,
    organizationId: string
): Promise<TagStatus> => {
    return queryTagStatus(gatewayId, roomName, "room", organizationId);
};
